import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { submitCredentials } from '../../services/api';
import CelebrationScreen from '../../components/portal/CelebrationScreen';
import { ArrowRight, ShieldCheck, ExternalLink, Loader2, Plus, Trash2, Lock } from 'lucide-react';

const Credentials = () => {
    const { token, clientData } = useOutletContext();
    const navigate = useNavigate();

    const [entries, setEntries] = useState([{ id: 1, integration: '', link: '', notes: '' }]);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const isComplete = clientData?.progress?.credentials_submitted || clientData?.completed_steps?.includes('credentials');

    const addEntry = () => {
        setEntries([...entries, { id: Date.now(), integration: '', link: '', notes: '' }]);
    };

    const removeEntry = (id) => {
        if (entries.length > 1) {
            setEntries(entries.filter(e => e.id !== id));
        }
    };

    const updateEntry = (id, field, value) => {
        setEntries(entries.map(e => e.id === id ? { ...e, [field]: value } : e));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            // Pass the formatted credential block to the Serverless function
            await submitCredentials(token, clientData, entries.map(e => ({
                integration: e.integration,
                link: e.link,
                notes: e.notes
            })));

            setSuccess(true);

        } catch (err) {
            console.error(err);
            setError(err.message || 'An unexpected error occurred. Please try again or contact support.');
            setSubmitting(false);
        }
    };

    return (
        <div className="animate-fade-in max-w-2xl mx-auto pb-24">
            <header className="mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#10B981]/10 text-[#10B981] font-data text-xs tracking-widest rounded-full mb-6">
                    <ShieldCheck className="w-4 h-4" /> ZERO-TRUST PROTOCOL
                </div>
                <h1 className="text-3xl font-bold mb-4">Secure Credential Submission</h1>
                <div className="prose prose-invert prose-p:text-[#F0EFF4]/70">
                    <p>
                        Your build team requires API keys and access credentials to link your agent to your existing tools (CRM, phone system, etc.).
                        <strong> For your security, never email API keys.</strong>
                    </p>
                    <p>
                        We use <strong>Password Pusher</strong> to securely transmit these keys. They are delivered directly to your isolated deployment environment and never stored in our web database.
                    </p>
                </div>
            </header>

            {success ? (
                <CelebrationScreen
                    emoji="🔐"
                    title="Credentials Secured!"
                    subtitle="Your build team has been notified. For security, this portal retains no record of your keys. You're one step closer to launch."
                    nextLabel="Return to Portal"
                    onNext={() => navigate(`/onboard/${token}`)}
                    intensity="medium"
                />
            ) : (
                <>
                    {/* Locked Banner */}
                    {isComplete && (
                        <div className="bg-[#18181B] border border-[#F0EFF4]/10 p-5 rounded-2xl mb-8 flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0 mt-0.5">
                                <Lock className="w-5 h-5 text-[#10B981]" />
                            </div>
                            <div>
                                <h3 className="font-bold text-[#F0EFF4] mb-1">Credentials Submitted</h3>
                                <p className="text-[#F0EFF4]/60 text-sm">
                                    Your credentials have been securely received. This form is locked for your security. Contact us if you need to provide additional keys.
                                </p>
                            </div>
                        </div>
                    )}
                    {/* Method Overview Container */}
                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 p-6 sm:p-8 rounded-3xl mb-12">
                        <h2 className="text-xl font-bold mb-6">Instructions</h2>
                        <ol className="list-decimal list-outside ml-4 space-y-4 text-[#F0EFF4]/80 text-sm">
                            <li>Keep this page open.</li>
                            <li>
                                Click the button below to open Password Pusher in a new tab. Paste <strong>one API key or password</strong> into the tool and click <span className="text-[#F0EFF4] font-bold">Push It!</span>
                            </li>
                            <li>Copy the securely generated link (`https://pwpush.com/p/...`).</li>
                            <li>Paste that link into the form below, along with what it's for.</li>
                        </ol>

                        <a
                            href="https://pwpush.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-magnetic mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#1A1A1A] border border-[#F0EFF4]/20 hover:border-[#7B61FF] text-[#F0EFF4] font-bold rounded-xl transition-colors text-sm"
                        >
                            Open Password Pusher <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>

                    {/* Submission Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <fieldset disabled={isComplete} className="space-y-6 disabled:opacity-80">
                            <h3 className="text-xl font-bold mb-4 border-b border-[#F0EFF4]/10 pb-4">Submit Secure Links</h3>

                            {entries.map((entry, index) => (
                                <div key={entry.id} className="p-6 bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-2xl relative">
                                    {entries.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeEntry(entry.id)}
                                            className="absolute top-4 right-4 text-[#F0EFF4]/30 hover:text-[#E63B2E] transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}

                                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-xs font-bold text-[#F0EFF4]/60 mb-2 uppercase tracking-wide">Integration Name</label>
                                            <input
                                                required
                                                placeholder="e.g. HubSpot API Key"
                                                value={entry.integration}
                                                onChange={(e) => updateEntry(entry.id, 'integration', e.target.value)}
                                                className="w-full bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl px-4 py-3 text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none transition-colors text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-[#F0EFF4]/60 mb-2 uppercase tracking-wide">PwPush Link</label>
                                            <input
                                                required
                                                type="url"
                                                placeholder="https://pwpush.com/p/..."
                                                value={entry.link}
                                                onChange={(e) => updateEntry(entry.id, 'link', e.target.value)}
                                                className="w-full bg-[#18181B] border border-[#7B61FF]/30 rounded-xl px-4 py-3 text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none transition-colors text-sm font-data"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-[#F0EFF4]/60 mb-2 uppercase tracking-wide">Notes (Optional)</label>
                                        <input
                                            placeholder="e.g. Read/Write permissions granted"
                                            value={entry.notes}
                                            onChange={(e) => updateEntry(entry.id, 'notes', e.target.value)}
                                            className="w-full bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl px-4 py-2 text-[#F0EFF4] focus:border-[#7B61FF] focus:outline-none transition-colors text-sm"
                                        />
                                    </div>
                                </div>
                            ))}

                            {!isComplete && (
                                <button
                                    type="button"
                                    onClick={addEntry}
                                    className="w-full py-4 border border-dashed border-[#F0EFF4]/20 text-[#F0EFF4]/60 hover:text-[#F0EFF4] hover:border-[#7B61FF] rounded-2xl flex items-center justify-center gap-2 text-sm font-bold transition-colors"
                                >
                                    <Plus className="w-4 h-4" /> Add Another Credential
                                </button>
                            )}

                            <div className="pt-8 mt-8 border-t border-[#7B61FF]/30">
                                {error && (
                                    <div className="mb-6 p-4 bg-[#E63B2E]/10 border border-[#E63B2E]/30 rounded-xl flex items-start gap-3 text-[#E63B2E]">
                                        <div className="shrink-0 mt-0.5">
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div className="flex-1 text-sm font-medium">
                                            {error}
                                        </div>
                                    </div>
                                )}
                                {!isComplete && (
                                    <button disabled={submitting} type="submit" className="w-full sm:w-auto px-8 py-4 bg-[#7B61FF] hover:bg-[#6b52e8] text-white font-bold rounded-xl flex items-center justify-center gap-3 transition-colors disabled:opacity-50 tracking-wide shadow-[0_0_20px_rgba(123,97,255,0.3)]">
                                        {submitting ? (
                                            <> <Loader2 className="w-5 h-5 animate-spin" /> Securing... </>
                                        ) : (
                                            <> Submit Secure Links <ArrowRight className="w-5 h-5" /> </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </fieldset>
                    </form>
                </>
            )}
        </div>
    );
};

export default Credentials;
